/**
 * GraphQL query for OpenTripPlanner v2 (OTPv2) trip planning.
 * References:
 * - OTP API Documentation: https://docs.opentripplanner.org/api/dev-2.x/graphql-gtfs/queries/plan
 * - Test endpoint: https://maps8.trimet.org/graphiql
 * - Internal documentation: https://trimet.atlassian.net/wiki/spaces/PRJ77/pages/393511117/GraphQL+API+Background
 */
export const TRIP_PLAN_QUERY = /* graphql */ `
  query Plan(
    $allowedVehicleRentalNetworks: [String],
    $arriveBy: Boolean,
    $banned: InputBanned,
    $bikeReluctance: Float,
    $bikeSpeed: Float,
    $carReluctance: Float,
    $date: String,
    $fromPlace: String!,
    $modes: [TransportMode],
    $searchWindow: Long,
    $optimize: OptimizeType,
    $time: String,
    $toPlace: String!,
    $walkReluctance: Float,
    $walkSpeed: Float
  ) {
  plan(
    allowedVehicleRentalNetworks: $allowedVehicleRentalNetworks
    arriveBy: $arriveBy
    banned: $banned
    bikeReluctance: $bikeReluctance
    bikeSpeed: $bikeSpeed
    carReluctance: $carReluctance
    date: $date
    fromPlace: $fromPlace
    locale: "en"
    searchWindow: $searchWindow
    time: $time
    toPlace: $toPlace
    transportModes: $modes
    optimize: $optimize
    walkReluctance: $walkReluctance
    walkSpeed: $walkSpeed
  ) {
    itineraries {
      accessibilityScore
      walkTime
      walkDistance
      duration
      endTime
      legs {
        accessibilityScore
        agency {
          alerts {
            alertDescriptionText
            alertHeaderText
            alertUrl
            effectiveStartDate
            id
          }
          fareUrl
          gtfsId
          id: gtfsId
          name
          timezone
          url
        }
        alerts {
          alertDescriptionText
          alertHeaderText
          alertUrl
          effectiveStartDate
          id
        }
        arrivalDelay
        departureDelay
        distance
        dropOffBookingInfo {
          contactInfo {
            bookingUrl
            infoUrl
            phoneNumber
          }
          earliestBookingTime {
            daysPrior
            time
          }
          latestBookingTime {
            daysPrior
            time
          }
          message
        }
        dropoffType
        duration
        endTime
        fareProducts {
          id
          product {
            __typename
            id
            medium {
              id
              name
            }
            name
            riderCategory {
              id
              name
            }
            ... on DefaultFareProduct {
              price {
                amount
                currency {
                  code
                  digits
                }
              }
            }
            ... on DependentFareProduct {
              dependencies {
                id
              }
              price {
                amount
                currency {
                  code
                }
              }
            }
          }
        }
        from {
          lat
          lon
          name
          vehicleRentalStation {
            name
            rentalNetwork {
              networkId
            }
          }
          rentalVehicle {
            id
            network
          }
          stop {
            alerts {
              alertDescriptionText
              alertHeaderText
              alertUrl
              effectiveStartDate
              id
            }
            code
            gtfsId
            id
            lat
            lon
          }
          vertexType
        }
        headsign
        interlineWithPreviousLeg
        intermediateStops {
          lat
          locationType
          lon
          name
          stopCode: code
          stopId: id
        }
        legGeometry {
          length
          points
        }
        mode
        pickupBookingInfo {
          contactInfo {
            bookingUrl
            infoUrl
            phoneNumber
          }
          earliestBookingTime {
            daysPrior
            time
          }
          latestBookingTime {
            daysPrior
            time
          }
          message
        }
        pickupType
        realTime
        realtimeState
        rentedBike
        rideHailingEstimate {
          arrival
          maxPrice {
            amount
            currency {
              code
            }
          }
          minPrice {
            amount
            currency {
              code
            }
          }
          provider {
            id
          }
        }
        route {
          alerts {
            alertDescriptionText
            alertHeaderText
            alertUrl
            effectiveStartDate
            id
          }
          color
          gtfsId
          id: gtfsId
          longName
          shortName
          textColor
          type
        }
        startTime
        steps {
          absoluteDirection
          alerts {
            alertDescriptionText
            alertHeaderText
            alertUrl
            effectiveStartDate
            id
          }
          area
          distance
          elevationProfile {
            distance
            elevation
          }
          lat
          lon
          relativeDirection
          stayOn
          streetName
        }
        stopCalls {
          stopLocation {
            __typename
          }
        }
        to {
          lat
          lon
          name
          vehicleRentalStation {
            name
            rentalNetwork {
              networkId
            }
          }
          rentalVehicle {
            id
            network
          }
          stop {
            alerts {
              alertDescriptionText
              alertHeaderText
              alertUrl
              effectiveStartDate
              id
            }
            code
            gtfsId
            id
            lat
            lon
          }
          vertexType
        }
        transitLeg
        trip {
          arrivalStoptime {
            stop {
              gtfsId
              id
            }
            stopPosition
          }
          blockId
          departureStoptime {
            stop {
              gtfsId
              id
            }
            stopPosition
          }
          gtfsId
          id
        }
      }
      startTime
      transfers: numberOfTransfers
      waitingTime
      walkTime
    }
    routingErrors {
      code
      description
      inputField
    }
  }
}
`;

export const ROUTE_COLOR_QUERY = /* graphql */ `
  query RouteColorQuery {
    routes {
      gtfsId
      color
    }
  }
`;
